import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, Portal, Surface, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { getConsent, setConsent } from '../lib/consent';
import { enableAnalytics, disableAnalytics } from '../lib/analytics';

const ConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let cancelled = false;
    getConsent().then((state) => {
      if (!cancelled && state === 'unknown') setVisible(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (Platform.OS !== 'web' || !visible) return null;

  const onAccept = async () => {
    setVisible(false);
    await setConsent('accepted');
    await enableAnalytics();
  };

  const onReject = async () => {
    setVisible(false);
    await setConsent('rejected');
    disableAnalytics();
  };

  return (
    <Portal>
      <View style={styles.wrapper} pointerEvents="box-none">
        <Surface style={styles.banner} elevation={4}>
          <Text style={styles.message}>{t('consent.message')}</Text>
          <View style={styles.buttons}>
            <Button mode="text" onPress={onReject} textColor={palette.textSecondary}>
              {t('consent.reject')}
            </Button>
            <Button mode="contained" onPress={onAccept}>
              {t('consent.accept')}
            </Button>
          </View>
        </Surface>
      </View>
    </Portal>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    wrapper: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      padding: spacing.md,
      alignItems: 'center',
    },
    banner: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: p.border,
      padding: spacing.lg,
      width: '100%',
      maxWidth: 720,
      gap: spacing.md,
    },
    message: {
      color: p.textPrimary,
      fontSize: 14,
      lineHeight: 20,
    },
    buttons: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
  });

export default ConsentBanner;
